import styled from '@emotion/styled'
import { css } from '@emotion/react'
import { Heading, Text } from '@leroy-merlin-br/backyard-react-ui'

const sectionStyles = ({ theme }) => css`
  label: section;

  padding: ${theme.spacings.zetta} 0 100px 0;

  color: ${theme.colors.white};

  background: linear-gradient(
    156deg,
    ${theme.colors.f600} 0%,
    ${theme.colors.v500} 100%
  );

  &::after {
    content: '';

    display: block;

    position: absolute;
    bottom: -2%;
    left: 0;
    right: 0;

    width: 100%;
    height: 100%;

    background: url(assets/images/lmcv/landing-page-2019/pattern-icons.svg);
    background-size: contain;
    background-repeat: no-repeat;
    background-position: bottom;
  }

  ${theme.media.kilo} {
    position: relative;

    padding: ${theme.spacings.zetta} 0;
  }
`

const descriptionStyles = ({ theme }) => css`
  color: ${theme.colors.f300};
`

const cardStyles = ({ theme }) => css`
  label: card;

  position: relative;

  width: 65%;
  max-width: 190px;

  margin-left: ${theme.spacings.tera};
  padding: ${theme.spacings.tera};

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;

  color: ${theme.colors.white};
  text-align: center;

  border-radius: ${theme.borderRadius.mega};
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2), 0 2px 4px 0 rgba(0, 0, 0, 0);

  background: ${theme.colors.v600};

  &::before {
    content: '';
    display: block;

    position: absolute;
    left: -10px;
    top: 8%;

    z-index: ${theme.zIndex.backdrop};

    height: 20px;
    width: 20px;

    border-radius: ${theme.borderRadius.kilo};
    background: ${theme.colors.v600};

    box-shadow: -1px 2px 4px 0 rgba(0, 0, 0, 0.2),
      -1px 2px 4px 0 rgba(0, 0, 0, 0);

    transform: rotateZ(45deg);
  }

  ${theme.media.mega} {
    max-width: 190px;
    min-height: 280px;

    margin: 0;
    width: 100%;

    &::before {
      left: 0;
      right: 0;
      top: -3%;

      margin: 0 auto;

      box-shadow: none;
    }
  }
`

const cardTitleStyles = ({ theme }) => css`
  font-weight: ${theme.font.weight.semibold};
`

const cardDescriptionStyles = ({ theme }) => css`
  color: ${theme.colors.f300};
`

const cardIconStyles = ({ theme }) => css`
  label: card__icon;

  svg {
    display: block;
    fill: ${theme.colors.f100};

    width: 50px;
  }
`

const cardListStyles = ({ theme }) => css`
  label: card__list;

  counter-reset: list-counter;

  position: relative;

  padding: 0;
  margin-top: ${theme.spacings.tera};

  &::before {
    content: '';
    display: block;

    width: 3px;
    height: 68%;

    position: absolute;
    top: ${theme.spacings.exa};
    left: 20px;

    z-index: ${theme.zIndex.backdrop};

    background: ${theme.colors.f600};
  }

  ${theme.media.mega} {
    display: flex;
    align-self: flex-start;

    &::before {
      width: calc(100% - 190px);
      height: 3px;

      top: ${theme.spacings.byte};
      left: 0;
      right: 0;

      margin: 0 auto;
    }
  }
`

const cardItemStyles = ({ theme }) => css`
  label: card__item;

  counter-increment: list-counter;

  list-style: none;

  position: relative;

  display: flex;

  margin-bottom: ${theme.spacings.exa};

  &:last-child {
    margin: 0;
  }

  &::before {
    content: counter(list-counter);
    display: inline;

    position: relative;
    top: ${theme.spacings.kilo};

    z-index: ${theme.zIndex.backdrop};

    height: 25px;
    width: 25px;

    border-radius: ${theme.borderRadius.circle};
    background: ${theme.colors.white};
    color: ${theme.colors.f600};

    text-align: center;

    font-size: ${theme.typography.text.kilo.fontSize};
    font-weight: ${theme.font.weight.semibold};
    line-height: ${theme.typography.text.mega.lineHeight};

    border: 8px solid ${theme.colors.f600};
    box-sizing: content-box;

    box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);
  }

  ${theme.media.mega} {
    flex-direction: column;
    align-items: center;

    margin: 0;

    &::before {
      top: -${theme.spacings.kilo};
      left: initial;

      margin-bottom: ${theme.spacings.kilo};
    }

    &:not(:last-child) {
      margin-right: ${theme.spacings.mega};
    }
  }

  ${theme.media.tera} {
    &:not(:last-child) {
      margin-right: ${theme.spacings.tera};
    }
  }
`

const containerPhoneStyles = ({ theme }) => css`
  label: container-phone;

  position: absolute;
  bottom: 0;
  right: 0%;

  ${theme.media.giga} {
    top: -30%;
    width: 350px;
    right: -22%;
  }

  ${theme.media.tera} {
    top: -38%;
    width: 410px;
    right: -10%;
  }
`

const imgPhoneStyles = () => css`
  label: container-phone__img-phone;

  width: 100%;
  height: auto;
`

const containerContentStyles = ({ theme }) => css`
  label: section__container-content;

  position: relative;
  display: flex;
  justify-content: center;

  ${theme.media.giga} {
    justify-content: flex-start;
  }
`

export const Section = styled('section')(sectionStyles)
export const Description = styled(Text)(descriptionStyles)
export const Card = styled('article')(cardStyles)
export const CardTitle = styled(Heading)(cardTitleStyles)
export const CardDescription = styled(Text)(cardDescriptionStyles)
export const CardIcon = styled('figure')(cardIconStyles)
export const CardList = styled('ol')(cardListStyles)
export const CardItem = styled('li')(cardItemStyles)
export const ContainerPhone = styled('figure')(containerPhoneStyles)
export const ImgPhone = styled('img')(imgPhoneStyles)
export const ContainerContent = styled('div')(containerContentStyles)
